import React, { Suspense } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { ArrowRight, Film, Youtube, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';

const Spline = React.lazy(() => import('@splinetool/react-spline'));

const Hero = () => {
  const { t } = useLanguage();

  return (
    <section className="relative min-h-screen bg-[#0B0F0E] overflow-hidden pt-20">
      {/* Subtle Grid Pattern Overlay */}
      <div
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage: `
            repeating-linear-gradient(0deg, transparent, transparent 1px, transparent 1px, transparent 7.6923%),
            repeating-linear-gradient(-90deg, #52ea88, #52ea88 1px, transparent 1px, transparent 7.6923%)
          `,
          backgroundSize: '100% 100%',
        }}
      />

      {/* Subtle Gradient Accent - reduced intensity */}
      <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-[#52ea88]/2 via-transparent to-transparent" />

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-[7.6923%] min-h-screen flex items-center">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-0 items-center w-full py-20 lg:py-0">
          {/* Left Content */}
          <div className="space-y-8">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 border border-[#52ea88]/20 bg-[#1a4a2e]/30">
              <span className="w-2 h-2 bg-[#52ea88] animate-pulse rounded-full"></span>
              <span className="text-[#52ea88] text-sm font-medium tracking-wider uppercase">
                Film Analysis Platform
              </span>
            </div>

            {/* Title */}
            <h1 className="text-5xl md:text-6xl lg:text-[66px] font-semibold text-[#E6FFF6] leading-[1.1] tracking-[-0.62px]">
              {t('hero.title')}
            </h1>

            {/* Tagline - "Meaning" accented only */}
            <p className="text-2xl md:text-3xl font-light text-[#E6FFF6]/80">
              Beyond Ratings. Into <span className="text-[#52ea88] font-medium">Meaning</span>.
            </p>

            {/* Description */}
            <p className="text-lg text-[#9FBFB4] max-w-lg leading-relaxed">
              {t('hero.description')}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col gap-4 pt-4">
              {/* Primary CTA */}
              <Link
                to="/reviews"
                className="group inline-flex items-center justify-between gap-3 px-6 py-4 bg-[#52ea88] text-[#0B0F0E] font-medium text-lg hover:bg-[#7aff9e] transition-all duration-300 w-fit min-w-[260px]"
              >
                <span>Explore Reviews Now</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>

              {/* Secondary CTAs */}
              <div className="flex flex-wrap gap-3">
                <a
                  href="https://moviematrixtrailers.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center gap-2 px-5 py-3 bg-[#1a4a2e]/50 text-[#E6FFF6] font-medium hover:bg-[#52ea88]/10 hover:text-[#52ea88] transition-all duration-300 border border-[#52ea88]/20 hover:border-[#52ea88]/50"
                >
                  <Film className="w-4 h-4" />
                  <span>Go to Trailers</span>
                  <ExternalLink className="w-3 h-3 opacity-60" />
                </a>
                <a
                  href="https://www.youtube.com/@TheMovieMatrixTV"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center gap-2 px-5 py-3 bg-[#1a4a2e]/50 text-[#E6FFF6] font-medium hover:bg-[#52ea88]/10 hover:text-[#52ea88] transition-all duration-300 border border-[#52ea88]/20 hover:border-[#52ea88]/50"
                >
                  <Youtube className="w-4 h-4" />
                  <span>Subscribe on YouTube</span>
                  <ExternalLink className="w-3 h-3 opacity-60" />
                </a>
              </div>
            </div>
          </div>

          {/* Right - Spline 3D Element */}
          <div className="relative hidden lg:flex items-center justify-center">
            <div className="w-[700px] h-[700px] overflow-visible relative">
              <Suspense
                fallback={
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="w-16 h-16 border-2 border-[#52ea88] border-t-transparent animate-spin"></div>
                  </div>
                }
              >
                <Spline scene="https://prod.spline.design/NbVmy6DPLhY-5Lvg/scene.splinecode" />
              </Suspense>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Gradient Fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#0B0F0E] to-transparent" />
    </section>
  );
};

export default Hero;
